// The vehicle tool strip (spec 2026-09-28-farm-3-vehicles-design.md §2): ✋ Ręka · Traktor ·
// Siewnik · Kombajn down the right edge, the fuel chip under them and — when the queue is
// waiting for fuel — the "Zatankuj — 50 💰" button. Phaser, fixed to the screen like FarmHud.
import { GAME_WIDTH, GAME_HEIGHT } from '../config.js';
import { gameState } from '../state/gameState.js';
import { UI_DEPTH } from '../ui/ySort.js';
import { fuelNow } from './farmRules.mjs';
import { toolButtons, fuelChip, toolArtKey, toolSeedBadge } from './vehicles.mjs';
import { ensureLoaded } from '../ui/sceneLoading.js';
import { gameButton, gameCard } from '../ui/gameUi.js';
import { cropCard, cropCardInfo, cropCardFoot, fitScale } from './farmMarket.mjs';
import { PALETTE } from '../ui/gameUiStyle.mjs';
import { iconText } from '../ui/iconText.js';
import { serverNow } from '../state/serverClock.mjs';

// A rounded wooden tile (the game look, gameUi.js): green ring when active, faded when locked.
export function paintTile(g, x, y, w, h, { active, enabled }) {
  g.clear();
  g.fillStyle(0x000000, 0.25); g.fillRoundedRect(x - w / 2 + 2, y - h / 2 + 5, w, h, 14);
  g.fillStyle(active ? 0x6cbf45 : PALETTE.wood.dark, 1); g.fillRoundedRect(x - w / 2, y - h / 2, w, h, 14);
  g.fillStyle(enabled ? PALETTE.wood.fill : 0x8c7560, 1); g.fillRoundedRect(x - w / 2 + 4, y - h / 2 + 4, w - 8, h - 8, 11);
  g.fillStyle(PALETTE.wood.light, enabled ? 0.55 : 0.25); g.fillRoundedRect(x - w / 2 + 10, y - h / 2 + 7, w - 20, 5, 3);
}

function paintPill(g, x, y, w, h, alarm) {
  g.clear();
  g.fillStyle(0x000000, 0.25); g.fillRoundedRect(x - w / 2 + 2, y - h / 2 + 4, w, h, h / 2);
  g.fillStyle(alarm ? 0xe0604d : PALETTE.wood.dark, 1); g.fillRoundedRect(x - w / 2, y - h / 2, w, h, h / 2);
  g.fillStyle(alarm ? 0xb8402f : 0x3d2a1a, 1); g.fillRoundedRect(x - w / 2 + 3, y - h / 2 + 3, w - 6, h - 6, h / 2 - 3);
}

const FONT = 'system-ui, -apple-system, sans-serif';
const X = GAME_WIDTH - 62, W = 104, H = 74, GAP = 8;
const N_TILES = 5;                           // Ręka, Zasyp, the three vehicles
const TOP = GAME_HEIGHT / 2 - ((N_TILES * H + (N_TILES - 1) * GAP) / 2) - 40;
// Where tile `i` of the strip stands — a friend's farm draws its one Ręka tile in the first slot (VisitFarmScene).
export const TOOL_TILE = { x: X, w: W, h: H, y: (i) => TOP + i * (H + GAP) + H / 2 };
const EMOJI = { hand: '{hand}', recycle: '{shovel}', tractor: '{tractor}', seeder: '{seedling}', harvester: '{wheat}' };

export class FarmToolStrip {
  constructor(scene, { getTool, onPick, onRefuel, getSeed = () => null, getColor = () => null }) {
    this.scene = scene;
    this.getTool = getTool;
    this.getSeed = getSeed;
    this.getColor = getColor;
    this._loading = new Set();
    this.paused = false;
    this.items = [];
    const n = N_TILES, top = TOP;
    const fix = (o, d = 0) => o.setScrollFactor(0).setDepth(UI_DEPTH + d);
    for (let i = 0; i < n; i++) {
      const y = top + i * (H + GAP) + H / 2;
      const tile = fix(scene.add.graphics());
      const bg = fix(scene.add.rectangle(X, y, W, H, 0xffffff, 0.001).setInteractive({ useHandCursor: true }));   // the hit area
      const icon = fix(iconText(scene, X, y - 10, '', { fontSize: '30px' }).setOrigin(0.5), 1);
      const img = fix(scene.add.image(X, y - 10, '__DEFAULT').setVisible(false), 1);
      const label = fix(iconText(scene, X, y + 24, '', { fontFamily: FONT, fontSize: '14px', color: '#fff8e8', fontStyle: 'bold', stroke: '#5a3414', strokeThickness: 3 }).setOrigin(0.5), 1);
      // the seed the Siewnik will plant: a small round badge on the tile's top-right corner
      const bx = X + W / 2 - 10, by = y - H / 2 + 10;
      const badgeBg = fix(scene.add.circle(bx, by, 17, 0xfff8e8).setStrokeStyle(3, 0x5a3414).setVisible(false), 2);
      const badge = fix(scene.add.image(bx, by, '__DEFAULT').setVisible(false), 3);
      // hovering the badge says what the seed is — the market's card for it (owner 2026-10-06); a tap shows it on phones
      badgeBg.setInteractive({ useHandCursor: true });
      badgeBg.on('pointerover', () => this._showSeedTip(by));
      badgeBg.on('pointerout', () => this._hideSeedTip());
      badgeBg.on('pointerup', (_p, _x, _y, ev) => { ev?.stopPropagation?.(); this._showSeedTip(by); this._tipTimer?.remove(); this._tipTimer = scene.time.delayedCall(2500, () => this._hideSeedTip()); });
      const item = { bg, tile, y, icon, img, label, badgeBg, badge, id: null };
      bg.on('pointerup', () => { if (item.id) onPick(item.id); });
      this.items.push(item);
    }
    const chipY = top + n * (H + GAP) + 22;
    this._chipY = chipY;
    this.chipBg = fix(scene.add.graphics());
    this.chipText = fix(iconText(scene, X, chipY, '', { fontFamily: FONT, fontSize: '18px', color: '#fff', fontStyle: 'bold' }).setOrigin(0.5), 1);
    this.refill = fix(gameButton(scene, X - W / 2 - 115, chipY, 210, 44, '', () => { if (this._refillEnabled) onRefuel(this._refillKind); }, { variant: 'danger', fontSize: 16 })).setVisible(false);
    this._pulse = scene.tweens.add({ targets: this.chipBg, alpha: 0.4, duration: 450, yoyo: true, repeat: -1, paused: true });
    this._unsub = gameState.on(() => this.refresh());
    this._timer = scene.time.addEvent({ delay: 1000, loop: true, callback: () => this.refresh() });
    scene.events.once('shutdown', () => this.destroy());
    this.refresh();
  }

  setPaused(on) { this.paused = on; this.refresh(); }

  _showSeedTip(y) {
    this._hideSeedTip();
    const raw = gameState.raw, id = this.getSeed();
    const c = id && cropCard(raw?.catalog?.farm, id, { level: raw?.level ?? 1, mastery: raw?.farm?.mastery ?? {} });
    if (!c) return;
    const s = this.scene, w = 230, h = 150, cx = X - W / 2 - 14 - w / 2, cy = Math.max(h / 2 + 8, y + 10);
    const card = gameCard(s, cx, cy, w, h);
    const name = iconText(s, 0, -h / 2 + 22, c.name, { fontFamily: FONT, fontSize: '19px', color: '#000', fontStyle: 'bold' }).setOrigin(0.5);
    const info = iconText(s, 0, 4, cropCardInfo(c), { fontFamily: FONT, fontSize: '14px', color: '#333', align: 'center', lineSpacing: 2 }).setOrigin(0.5);
    info.setScale(fitScale(info.width, w - 20));
    const foot = iconText(s, 0, h / 2 - 18, cropCardFoot(c), { fontFamily: FONT, fontSize: '15px', color: c.locked ? '#777' : '#b8860b', fontStyle: 'bold' }).setOrigin(0.5);
    card.add([name, info, foot]);
    this._tip = card.setScrollFactor(0).setDepth(UI_DEPTH + 10);
  }

  _hideSeedTip() { this._tip?.destroy(); this._tip = null; }

  // a picture the strip needs that is not downloaded yet: fetch it once, then redraw
  _load(key) {
    if (this._loading.has(key)) return;
    this._loading.add(key);
    ensureLoaded(this.scene, [key]).then(() => { if (this.items[0]?.bg.active) this.refresh(); }).catch(() => {});
  }

  refresh() {
    const raw = gameState.raw;
    if (!raw?.farm || !raw.catalog?.farm?.tools) return;
    const active = this.getTool();
    toolButtons(raw).forEach((b, i) => {
      const it = this.items[i];
      it.id = b.id;
      const key = toolArtKey(b, raw);                 // always the machine itself, bought or not
      const hasArt = key && this.scene.textures.exists(key);
      if (key && !hasArt) this._load(key);
      it.img.setVisible(!!hasArt);
      if (hasArt) it.img.setTexture(key).setDisplaySize(64, 64 * this.scene.textures.get(key).getSourceImage().height / this.scene.textures.get(key).getSourceImage().width);
      it.icon.setText(hasArt ? '' : EMOJI[b.id]);
      it.label.setText(b.enabled ? (b.id === 'hand' ? 'Ręka' : b.id === 'recycle' ? 'Zasyp' : `${b.label} ${b.size}×${b.size}`) : b.buyable ? `{cart} ${b.label}` : `{lock} poz. ${b.lockLevel}`);
      paintTile(it.tile, X, it.y, W, H, { active: active === b.id, enabled: b.enabled });
      for (const o of [it.icon, it.img, it.label]) o.setAlpha(b.enabled ? 1 : 0.55);
      const bk = toolSeedBadge(b, this.getSeed(), this.getColor());
      const hasSeed = bk && this.scene.textures.exists(bk);
      if (bk && !hasSeed) this._load(bk);
      it.badgeBg.setVisible(!!hasSeed); it.badge.setVisible(!!hasSeed);
      if (!hasSeed && this._tip && b.id === 'seeder') this._hideSeedTip();
      if (hasSeed) {
        const src = this.scene.textures.get(bk).getSourceImage(), k = 26 / Math.max(src.width, src.height);
        it.badge.setTexture(bk).setDisplaySize(src.width * k, src.height * k);
      }
    });
    const vehicle = active !== 'hand' && active !== 'recycle';      // only vehicles use fuel
    const chip = fuelChip(fuelNow(raw.farm, raw.catalog.farm.fuel, serverNow()), { paused: this.paused, money: raw.money, cfg: raw.catalog.farm.fuel, cans: raw.farm.fuel_cans ?? 0 });
    paintPill(this.chipBg, X, this._chipY, W, 40, chip.alarm);
    this.chipBg.setVisible(vehicle); this.chipText.setVisible(vehicle).setText(chip.text);
    if (chip.alarm && vehicle) this._pulse.resume(); else { this._pulse.pause(); this.chipBg.setAlpha(1); }
    const r = vehicle ? chip.refill : null;
    this._refillEnabled = !!r?.enabled;
    this._refillKind = r?.kind;
    this.refill.setVisible(!!r);
    if (r) this.refill.setLabel(r.label).setVariant(r.kind === 'can' ? 'primary' : 'danger').setEnabled(r.enabled);
  }

  destroy() {
    this._hideSeedTip(); this._tipTimer?.remove();
    this._unsub?.(); this._unsub = null;
    this._timer?.remove();
    this._pulse?.remove();
    for (const it of this.items) for (const o of [it.bg, it.tile, it.icon, it.img, it.label, it.badgeBg, it.badge]) o.destroy();
    for (const o of [this.chipBg, this.chipText, this.refill]) o.destroy();
  }
}
